import { SectionII_ProjectSummary } from './sections/SectionII_ProjectSummary';
import { SectionIII_ProjectScope } from './sections/SectionIII_ProjectScope';
import { SectionIV_ContractTerms } from './sections/SectionIV_ContractTerms';
import { SectionV_Warranty } from './sections/SectionV_Warranty';
import { SectionVI_InvestmentSummary } from './sections/SectionVI_InvestmentSummary';
import type {
  AllowancesHandler,
  AssumptionsOverrideHandler,
  ContractTermsOverrideHandler,
  CostRowHandlers,
  DiscountHandlers,
  DetailsMutatedHandler,
  ElementInclusionHandler,
  ElementMarginHandler,
  ProjectDetailsMarginHandler,
  GenerateSummaryHandler,
  SummaryOverrideHandler,
  ProjectNameHandler,
  LineItemAddRequestHandler,
  LineItemCreateHandler,
  LineItemDeleteHandler,
  LineItemOpenHandler,
  LineItemPatchHandler,
  PaymentMilestonesHandler,
  ProjectLayoutData,
  ProjectLayoutMode,
  ScopeFieldValueHandler,
  ShowCostHistoryHandler,
  TimelineHandler,
} from './types';

/**
 * Sprint 41 §14.S10-Q1 — `<ProjectLayoutSections>` umbrella.
 *
 * Renders Sections II–VI of the Phase 0 §11 canonical 7-section
 * layout. Section I (header) + Section VII (signatures) stay
 * page-specific per S10-Q1 lock — pages render those around the
 * umbrella.
 *
 * Consumed by four pages, each passing a different `mode`:
 *
 *   estimate:  Estimate page (estimator finalizes pricing)
 *   review:    Review Screen (estimator approves intake)
 *   proposal:  Proposal public view (sales-rep discussion artifact;
 *              omits Sections IV + V per §14.S12-Q3)
 *   contract:  Contract public view (binding signature artifact;
 *              renders all sections)
 *
 * Each sub-section reads `mode` and adjusts edit affordances +
 * visibility per `capabilitiesForMode(mode)`.
 *
 * Wave 15 status: SCAFFOLD ONLY. Sub-components render structural
 * skeletons + read-only placeholders. Subsequent waves wire the rich
 * UX (editable line items, cost expansion, tooltip, panel, variation
 * selector, etc.).
 *
 * Once the rich UX lands, this umbrella becomes the single source of
 * truth for the project body; the existing 1652-line `EstimateView`
 * + 558-line `EstimatorReviewPage` shrink to thin wrappers around
 * `<ProjectLayoutSections mode="estimate" />` and `<ProjectLayoutSections mode="review" />`.
 */
export interface ProjectLayoutSectionsProps {
  data: ProjectLayoutData;
  mode: ProjectLayoutMode;
  /** Page-level Show all/Hide all toggle (§14.S10-Q7). Optional; defaults to undefined-state. */
  showAll?: boolean;
  onShowAllToggle?: (next: boolean) => void;
  /**
   * W24 — invoked when the user inline-edits a Section III line item
   * (description / quantity / etc.). Required for inline edits to render;
   * if omitted the rows fall back to read-only even in `mode="estimate"`.
   * The page wires this to its API client + state-refresh.
   */
  onLineItemPatch?: LineItemPatchHandler;
  /**
   * Founder mockup 2026-07-16 — invoked when the estimator adds a new
   * line item to a Section III element ("+ Add" under a CSF's assigned
   * SKUs or in the Administration block). Optional; omit for read-only
   * rendering (review / proposal / contract modes).
   */
  onLineItemCreate?: LineItemCreateHandler;
  /**
   * Delete a material/service line item (Section II Project Detail costs
   * and Section III assigned SKUs). Optional; omit for read-only.
   * Required for the post-save delete control — create without delete
   * left saved rows stuck (founder staging review 2026-07-29).
   */
  onLineItemDelete?: LineItemDeleteHandler;
  /**
   * Estimate drawer (founder 2026-07-30, Option 4c) — when supplied,
   * Section III line-item rows render read-only purpose-first and open
   * the page-level drawer; inline cost editing moves into the drawer.
   * Omit (review / proposal) to keep the legacy inline rendering.
   */
  onLineItemOpen?: LineItemOpenHandler;
  /**
   * Estimate drawer — routes "+ Add material / + Add service" into the
   * drawer's MPT search. Omit to keep the inline AddSkuInline row.
   */
  onLineItemAddRequest?: LineItemAddRequestHandler;
  /**
   * W25 — invoked when the user inline-edits, adds, or deletes a cost
   * row under a Section III line item. Bundled into one object to keep
   * the umbrella signature tidy. Optional; omit for read-only rendering.
   */
  onCostRow?: CostRowHandlers;
  /**
   * Founder requirement 2026-07 — invoked when the estimator commits an
   * inline edit on a Section III CSF row's value (selects get a dropdown
   * of the linked question's options). Required for the edit affordance
   * to render; omitted = read-only values, even in `mode="estimate"`.
   * The page wires this to PATCH /estimate/fields/:fieldId/value +
   * a resolve reload so the scope statements re-render.
   */
  onFieldValue?: ScopeFieldValueHandler;
  /**
   * W27 — invoked when the user clicks the history icon on a cost row.
   * The page owns the side-panel state; the umbrella just forwards the
   * request. Renders the history icon iff this prop is supplied AND
   * `caps.showCostHistoryLink` is true.
   */
  onShowCostHistory?: ShowCostHistoryHandler;
  /**
   * W28 — invoked when the user saves a new Section IV Contract Terms
   * body or resets to the admin-layer default. Pass null to reset. The
   * page wires this to PATCH /estimate/:projectId/contract.
   */
  onContractTermsOverride?: ContractTermsOverrideHandler;
  /**
   * W29 — Section VI discount-line CRUD handlers. When supplied AND
   * `caps.isEditable`, Section VI renders inline add/edit/delete for
   * project-level + element-level discounts.
   */
  onDiscount?: DiscountHandlers;
  /**
   * W30 — invoked when the user saves a payment-milestones list or
   * resets to the Settings-level default. Pass null to reset. The page
   * wires this to PATCH /estimate/:projectId/contract.
   */
  onPaymentMilestones?: PaymentMilestonesHandler;
  /**
   * Sprint 41 W32 enrichment Slice 1 — Section II Timeline editor.
   * When supplied AND `caps.isEditable`, Section II renders inline
   * editors for weeksUntilStart / weeksToComplete / tentativeStartDate.
   */
  onTimeline?: TimelineHandler;
  /**
   * Sprint 41 W32 enrichment Slice 2 — Section II Assumptions editor.
   * When supplied AND `caps.isEditable`, Section II renders the
   * rich-text editor + reset link for `Project.assumptionsOverride`.
   */
  onAssumptionsOverride?: AssumptionsOverrideHandler;
  /**
   * Sprint 41 W32 enrichment Slice 3 — Section II AI generate.
   * When supplied AND `caps.isEditable`, Section II shows a
   * "Generate with AI" button next to the Narrative editor. Handler
   * is expected to call the backend AI endpoint AND refresh layout
   * data on success. Never invoked on mount.
   */
  onGenerateSummary?: GenerateSummaryHandler;
  /**
   * Founder 2026-09-01 — persist the estimator-authored Narrative
   * (same PATCH /estimate/:projectId/contract as assumptions).
   */
  onSaveSummary?: SummaryOverrideHandler;
  onSaveProjectName?: ProjectNameHandler;
  /**
   * Project Details cost assignment — reload after material/service
   * line items are attached to a Section II ProjectDetail row via
   * POST /estimate/details/:detailId/line-items.
   */
  onDetailsMutated?: DetailsMutatedHandler;
  /**
   * Sprint 41 W32 enrichment Slice 5 — Section VI manual allowances
   * editor. When supplied AND `caps.isEditable`, Section VI renders
   * an add/edit/delete card for `Project.allowances`.
   */
  onAllowances?: AllowancesHandler;
  /** Task 18 Option B — Include / Phase later chips. */
  onElementInclusion?: ElementInclusionHandler;
  onElementMargin?: ElementMarginHandler;
  onProjectDetailsMargin?: ProjectDetailsMarginHandler;
}

export function ProjectLayoutSections({
  data,
  mode,
  showAll,
  onShowAllToggle,
  onLineItemPatch,
  onLineItemCreate,
  onLineItemDelete,
  onLineItemOpen,
  onLineItemAddRequest,
  onCostRow,
  onFieldValue,
  onShowCostHistory,
  onContractTermsOverride,
  onDiscount,
  onPaymentMilestones,
  onTimeline,
  onAssumptionsOverride,
  onGenerateSummary,
  onSaveSummary,
  onSaveProjectName,
  onDetailsMutated,
  onAllowances,
  onElementInclusion,
  onElementMargin,
  onProjectDetailsMargin,
}: ProjectLayoutSectionsProps) {
  return (
    <>
      <SectionII_ProjectSummary
        data={data}
        mode={mode}
        onTimeline={onTimeline}
        onAssumptionsOverride={onAssumptionsOverride}
        onGenerateSummary={onGenerateSummary}
        onSaveSummary={onSaveSummary}
        onSaveProjectName={onSaveProjectName}
        onDetailsMutated={onDetailsMutated}
        onLineItemDelete={onLineItemDelete}
      />
      <SectionIII_ProjectScope
        data={data}
        mode={mode}
        showAll={showAll}
        onShowAllToggle={onShowAllToggle}
        onElementInclusion={onElementInclusion}
        onLineItemPatch={onLineItemPatch}
        onLineItemCreate={onLineItemCreate}
        onLineItemDelete={onLineItemDelete}
        onLineItemOpen={onLineItemOpen}
        onLineItemAddRequest={onLineItemAddRequest}
        onCostRow={onCostRow}
        onFieldValue={onFieldValue}
        onShowCostHistory={onShowCostHistory}
      />
      <SectionIV_ContractTerms
        data={data}
        mode={mode}
        onContractTermsOverride={onContractTermsOverride}
      />
      <SectionV_Warranty data={data} mode={mode} />
      <SectionVI_InvestmentSummary
        data={data}
        mode={mode}
        onDiscount={onDiscount}
        onPaymentMilestones={onPaymentMilestones}
        onAllowances={onAllowances}
        onElementInclusion={onElementInclusion}
        onElementMargin={onElementMargin}
        onProjectDetailsMargin={onProjectDetailsMargin}
      />
    </>
  );
}

// Re-export types for consumers.
export type {
  ContractTermsOverrideHandler,
  CostRowHandlers,
  DiscountHandlers,
  ElementInclusionHandler,
  ElementMarginHandler,
  ProjectDetailsMarginHandler,
  LineItemAddRequestHandler,
  LineItemCreateHandler,
  LineItemCreateInput,
  LineItemDeleteHandler,
  LineItemOpenHandler,
  LineItemPatchHandler,
  LineItemPatchInput,
  PaymentMilestone,
  PaymentMilestonesHandler,
  ProjectLayoutData,
  ProjectLayoutMode,
  ScopeFieldValueHandler,
  ShowCostHistoryHandler,
  TimelineHandler,
  TimelineInput,
  GenerateSummaryHandler,
  DetailsMutatedHandler,
  AllowanceEntry,
  AllowancesHandler,
} from './types';
export { capabilitiesForMode } from './types';
